import { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DeleteShortCode from './DeleteShortCode';
import { fetchShortCodes } from 'api/short-codes';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';
import { SmsKeyword } from 'types/domain';

const columnHelper = createColumnHelper<SmsKeyword>();

const ShortCodeSettings = () => {
    const { shortCodeLimitIncrease = false } = useFlags();
    const { pagination, setPagination } = Table.useReactTableState();
    const { url: matchUrl } = useRouteMatch();
    const { accountId } = useCurrentAccount();

    const { data: codes } = useSuspenseQuery({
        queryKey: accountQueryKeys.shortCodesWithFilters(accountId, {
            page: pagination.pageIndex + 1,
        }),
        queryFn: () => fetchShortCodes(accountId, { page: pagination.pageIndex + 1 }),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('keyword', {
                header: 'Keyword',
                cell: info => <Badge>{info.getValue()}</Badge>,
            }),

            columnHelper.accessor('model.title', {
                header: 'Campaign',
                cell: info => <span className="max-w-[150px] truncate">{info.getValue()}</span>,
            }),

            columnHelper.accessor('statistics', {
                header: 'Stats',
                cell: info => {
                    const { count, supporters, raised } = info.getValue();
                    return (
                        <span className="max-w-xs truncate">
                            {count} texts, {supporters} supporters, ${raised}
                        </span>
                    );
                },
            }),

            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const keyword = info.row.original;

                    return (
                        <DropdownMenu.Root key={keyword.id}>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                <DropdownMenu.LinkItem to={`${matchUrl}/${keyword.id}/edit`}>
                                    Edit
                                </DropdownMenu.LinkItem>
                                <DeleteShortCode.Root keywordId={keyword.id}>
                                    <DeleteShortCode.Trigger asChild>
                                        <DropdownMenu.Item
                                            className="focus-visible:before:border-0"
                                            onSelect={e => e.preventDefault()}
                                        >
                                            Delete
                                        </DropdownMenu.Item>
                                    </DeleteShortCode.Trigger>
                                </DeleteShortCode.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [matchUrl],
    );

    const table = useReactTable({
        data: codes?.data || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        pageCount: codes?.meta?.last_page ?? -1,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, codes);

    const keywordLimitReached = (codes?.data.length || 0) >= (shortCodeLimitIncrease ? 50 : 5);

    return (
        <Table.Root variant="bordered" table={table}>
            <div className="flex flex-col gap-4">
                <Box className="overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-6 py-4">
                        <div className="col-span-full flex flex-col md:col-span-8">
                            <p className="text-2xl font-medium">Text-to-Donate</p>
                            <p className="text-sm">
                                Engage your supporters by having them text your keywords to 53-555.
                                We'll reply with a link to your campaign. Need help?{' '}
                                <a
                                    className="font-medium"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://help.givebutter.com/en/articles/4044807-how-to-configure-text-to-donate"
                                >
                                    Read the guide.
                                </a>
                            </p>
                        </div>
                        <div className="col-span-full flex justify-start md:col-span-4 md:justify-end">
                            <Link
                                to={`${matchUrl}/new`}
                                className={twMerge(keywordLimitReached && 'pointer-events-none')}
                            >
                                <Button variant="solid" size="sm" disabled={keywordLimitReached}>
                                    <FontAwesomeIcon icon={faPlus} size="sm" /> Add Keyword
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <Separator />
                    <div className="relative overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No keywords found</Table.EmptyTitle>
                        </Table.Empty>
                    </div>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </div>
        </Table.Root>
    );
};

export { ShortCodeSettings };
